import { Box, useDisclosure, useToast } from '@chakra-ui/react'
import React, { Fragment, useEffect, useState } from 'react'
import axios from "axios"
import { connect } from "react-redux";
import { compileEmailSource, fetchEmailSavedTemplate, getStriptCredentials, postEmailTemplate } from '../../Services/api';
import StripoHeader from './StripoHeader';
import { Helmet } from "react-helmet"
import { getCustomFonts, getDefaultMergeTags } from '../../_helpers/data_helper';
import SaveTemplateModal from './SaveTemplateModal';
import { useFormik } from 'formik';
import { saveEmailTemplate } from '../../_helpers/formValues';
import { saveEmailTemplateValidation } from '../../_helpers/validationSchema';
import { showSwal } from '../../_helpers/helpers';
import Swal from 'sweetalert2';
import _ from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../Services/axiosInstance';
import { compileEmailTemplate, createEmailTemplateFormData } from './emailTemplateExport';

function Editor({ emailId, userId, orgToken, closeModal, templateUuid, type }) {

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const location = useLocation();

  const [formInitialValues, setFormInitialValues] = useState(saveEmailTemplate);

  const getTemplateCallback = async (html, css) => {
    formik.setSubmitting(true);
    try {
      const compiledTemplate = await compileTemplate(html, css);
      const saveResponse = await save(compiledTemplate);

      if (!_.isEmpty(saveResponse)) {
        toast({
          title: `Template ${((!!uuid || !!templateUuid) && !location?.state?.isDuplicate && type !== 'duplicate') ? 'Updated' : 'Created'}.`,
          description: `We've ${((!!uuid || templateUuid) && !location?.state?.isDuplicate) ? 'updated' : 'created'} the template for you.`,
          status: 'success'
        });

        Swal.close();
        if (window.location.href.includes("all-banks")) {
          closeModal();
        } else {
          navigate("/email/list");
        }
      }
    } catch (error) {
      toast({
        title: 'Unable to save template',
        description: error.message,
        status: 'error'
      });
      Swal.close();
    } finally {
      formik.setSubmitting(false);
    }
  };

  const saveTemplate = (values, action) => {
    formik.setSubmitting(true);

    showSwal(
      Swal,
      "Are you sure?",
      `Do you want to ${((!!uuid || templateUuid) && !location?.state?.isDuplicate && type !== 'duplicate') ? 'update' : 'save'} the template?`,
      true,
      true,
      ((!!uuid || templateUuid) && !location?.state?.isDuplicate) ? 'Update' : 'Save',
      `Don't ${(!!uuid || templateUuid) ? 'update' : 'save'}`,
      async () => {
        window.StripoApi.setHiddenPreHeader(values.preheader);
        window.StripoApi.getHiddenPreHeader();
        return new Promise((resolve) => {
          window.StripoApi.getTemplate((html, css) => resolve(getTemplateCallback(html, css)));
        });
      },
      (res) => {
        if (res.isConfirmed) {
          formik.setSubmitting(false);
          Swal.close();
        }
        formik.setSubmitting(false);
      }
    );
  };

  const save = async (compiledTemplate) => {
    const fileFormData = createEmailTemplateFormData(formik.values, compiledTemplate);

    try {
      const saveResponse = await postEmailTemplate(
        fileFormData,
        uuid || templateUuid,
        orgToken,
        location?.state?.isDuplicate || type === 'duplicate'
      );
      return saveResponse;
    } catch (error) {
      console.error(error);
      toast({
        title: 'Oops',
        description: `Something went wrong while creating/updating the template. Please try again.`,
        status: 'error'
      });
      formik.setSubmitting(false);
      Swal.close();
      return null;
    }
  };

  const formik = useFormik({
    initialValues: { ...formInitialValues },
    validationSchema: saveEmailTemplateValidation,
    enableReinitialize: true,
    onSubmit: saveTemplate
  });

  let initPlugin = (template) => {
    const loggedInUserEmailId = JSON.parse(localStorage.getItem("userData")).email;

    const script = document.createElement("script");
    script.id = 'stripoScript';
    script.type = 'text/javascript';
    script.src = 'https://plugins.stripo.email/static/latest/stripo.js';
    script.onload = function () {
      window.Stripo.init({
        settingsId: "striptEditorContainer",
        previewId: "stripoPreviewContainer",
        locale: 'en',
        html: template.html,
        css: template.css,
        emailId: emailId,
        apiRequestData: {
          emailId: loggedInUserEmailId,
          userId: userId
        },
        userFullName: 'Plugin Demo User',
        codeEditorButtonId: 'codeEditor',
        undoButtonId: 'undoButton',
        redoButtonId: 'redoButton',
        externalImagesLibrary: window.ExternalImagesLibrary,
        ignoreClickOutsideSelectors: '#externalImagesLibrary',
        editorFonts: {
          "showDefaultStandardFonts": true,
          "showDefaultNotStandardFonts": true,
          "customFonts": getCustomFonts()
        },
        getAuthToken: async function (callback) {
          let tokenRes = await getStriptCredentials();
          if (tokenRes?.data?.token) {
            callback(tokenRes.data.token);
          } else {
            console.error('Stripo credentials issue — check config');
          }
        },
        onTemplateLoaded: function () {
          console.log('Stripo template loaded');
        },
        mergeTags: [
          {
            entries: getDefaultMergeTags() || []
          }
        ],
      });
    };
    document.body.appendChild(script);
  };

  let fetchInitialTemplate = async () => {
    const htmlLink = 'https://media.orbisdata.ai/email_templates/predefined/categories/Basic-Templates/Empty-Template/Empty-Template.html';
    const cssLink = 'https://media.orbisdata.ai/email_templates/predefined/categories/Basic-Templates/Empty-Template/Empty-Template.css';

    try {
      const [htmlRes, cssRes] = await Promise.all([
        axios.get(htmlLink),
        axios.get(cssLink)
      ]);

      if (htmlRes?.data && cssRes?.data) {
        initPlugin({ html: htmlRes.data, css: cssRes.data });
      } else {
        console.warn("Initial template not found");
      }
    } catch (err) {
      console.error("Failed to load initial template", err);
    }
  };

  const compileTemplate = (html, css) => compileEmailTemplate(window.StripoApi, html, css, (source) => {
    const loggedInUserEmailId = JSON.parse(localStorage.getItem('userData')).email;
    return compileEmailSource({
      ...source,
      apiRequestData: JSON.stringify({ emailId: loggedInUserEmailId, userId }),
    });
  });

  const previewTemplate = () => {
    window.StripoApi.getTemplate(async (html, css) => {
      try {
        const compiled = await compileTemplate(html, css);
        window.ExternalPreviewPopup.openPreviewPopup(compiled.body, compiled.ampHtml);
      } catch (error) {
        toast({ title: 'Unable to preview template', description: error.message, status: 'error' });
      }
    });
  };

  let b64DecodeUnicode = function (str) {
    return decodeURIComponent(atob(str).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  };

  let fetchSavedTemplate = async () => {
    try {
      const templateResp = await fetchEmailSavedTemplate(uuid || templateUuid, orgToken);

      if (!_.isEmpty(templateResp) && templateResp.data?.status === "success") {
        const data = templateResp.data;
        let dt = {
          description: data.description,
          subject: data.subject,
          body: data.body,
          preheader: data?.extraData?.preheaders || "",
          type: data?.extraData?.templateType || "stripo",
        };

        const extraData = data?.extraData;
        if (extraData?.templateType === 'stripo' && extraData?.stripoData) {
          let decodedHtml = extraData.stripoData.templateHtml || '';
          let decodedCss = extraData.stripoData.templateCss || '';

          try {
            decodedHtml = b64DecodeUnicode(extraData.stripoData.templateHtml);
          } catch (e) {
            console.warn("Failed to decode template HTML", e);
          }

          try {
            decodedCss = b64DecodeUnicode(extraData.stripoData.templateCss);
          } catch (e) {
            console.warn("Failed to decode template CSS", e);
          }

          initPlugin({
            html: decodedHtml,
            css: decodedCss
          });

          dt.stripoBody = {
            templateHtml: extraData.stripoData.templateHtml,
            templateCss: extraData.stripoData.templateCss
          };
          dt.blockids = data.blockids ? JSON.parse(data.blockids) : [];
        }

        setFormInitialValues({ ...saveEmailTemplate, ...dt });
      } else {
        navigate("/email/edit");
      }
    } catch (error) {
      toast({
        title: `Oops!`,
        description: `Problem fetching saved template.`,
        status: 'error'
      });
      if (!window.location.href.includes("all-banks")) {
        navigate("/email/edit");
      }
    }
  };

  useEffect(() => {
    if (!!uuid || !!templateUuid) {
      fetchSavedTemplate();
    } else {
      fetchInitialTemplate();
    }

    window.mktConfig = {
      apiEndpoint: process.env.REACT_APP_API_ENDPOINT,
      orgToken: orgToken || JSON.parse(localStorage.getItem('selectedOrg'))?.token,
      axios: axiosInstance
    };
  }, []);

  return (
    <Fragment>
      <StripoHeader previewTemplate={previewTemplate} onOpen={onOpen} />

      <Helmet>
        <link href={`${process.env.PUBLIC_URL}/css/externalPreview.css`} rel="stylesheet" />
        <script src={`${process.env.PUBLIC_URL}/js/stripoPreview.js`}></script>
        <script src={`${process.env.PUBLIC_URL}/js/email-editor-image-library.js`}></script>
      </Helmet>

      <Box mt={20} position={'relative'}>
        <Box id="striptEditorContainer" float={'left'} w={'400px'} />
        <Box id='stripoPreviewContainer' w={'calc(100% - 400px)'} float={'left'} />
      </Box>

      <SaveTemplateModal
        isDuplicate={type === 'duplicate' || location?.state?.isDuplicate}
        isOpen={isOpen}
        onClose={onClose}
        location={location}
        formik={formik}
        uuid={uuid || templateUuid}
      />
    </Fragment>
  )
}

const mapStateToProps = (state) => {
  return {
    ...state,
    emailId: state.auth.user_data.email,
    userId: state.auth.user_data.id,
  }
}

export default connect(mapStateToProps)(Editor)
